import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

type ButtonColor = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'danger-secondary';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-sm font-bold transition-colors focus-visible:outline-hidden focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      color: {
        primary:
          'border-none bg-primary text-white hover:border-none hover:bg-primary_darken hover:text-white',
        secondary:
          'border border-solid border-border bg-white text-main hover:bg-gray_darken hover:text-main',
        tertiary: 'border-none bg-transparent text-primary hover:bg-primary_lighten',
        danger: 'bg-danger text-white hover:bg-danger_darken hover:text-white',
        'danger-secondary':
          'border border-solid border-danger bg-white text-danger hover:bg-red-50 hover:text-danger',
      },
      disabled: {
        true: 'pointer-events-none cursor-not-allowed opacity-50',
        false: 'cursor-pointer',
      },
      active: {
        true: '',
        false: '',
      },
      size: {
        xs: 'size-5 gap-xxs text-xs',
        sm: 'h-8 gap-xxs px-xs text-button-sm',
        md: 'h-[42px] gap-xxs px-sm text-button-md',
        lg: 'h-[52px] gap-xs px-md text-button-lg',
      },
      full: {
        true: 'w-full',
      },
      iconOnly: {
        false: '',
      },
    },
    compoundVariants: [
      {
        color: 'primary',
        active: true,
        className: 'border-none bg-primary_darken text-white',
      },
      {
        color: 'secondary',
        active: true,
        className: 'bg-gray_darken text-main',
      },
      {
        color: 'tertiary',
        active: true,
        className: 'bg-primary_lighten',
      },
      {
        color: 'danger',
        active: true,
        className: 'bg-danger_darken text-white',
      },
      {
        color: 'danger-secondary',
        active: true,
        className: 'border border-solid border-danger bg-red-50 text-danger',
      },
      {
        size: 'xs',
        iconOnly: true,
        className: 'size-5 p-0!',
      },
      {
        size: 'sm',
        iconOnly: true,
        className: 'size-8 p-0!',
      },
      {
        size: 'md',
        iconOnly: true,
        className: 'size-[42px] p-0!',
      },
      {
        size: 'lg',
        iconOnly: true,
        className: 'size-[52px] p-0!',
      },
    ],
    defaultVariants: {
      color: 'primary',
      size: 'md',
      iconOnly: false,
      disabled: false,
      active: false,
      full: false,
    },
  }
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  color?: ButtonColor;
  disabled?: boolean;
  iconOnly?: boolean;
  active?: boolean;
  full?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, color, size, disabled, asChild = false, iconOnly, active, full, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ color, size, className, disabled, iconOnly, active, full }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
